import { useState, useMemo, FC } from 'react';
import {
  Container,
  ContentLayout,
  Header,
  FormField,
  Select,
  Button,
  SpaceBetween,
  Flashbar,
  Box,
  Grid,
  StatusIndicator,
  Alert,
  Table,
  Toggle,
} from '@amzn/awsui-components-react';
import { useGetTestSuitesQuery, useRunTestSuiteMutation, useUpdateTestSuiteScheduleMutation } from 'src/store/apis/testSuiteApi';
import {
  TestSuiteInfo,
  TestExecutionRequest,
  ToolVersionRevisionInfo
} from 'src/store/interfaces/TestSuite.types';
import useNotification from '../../hooks/useNotification';
import { usePermissions } from '../../hooks/usePermissions';
import BlueyDashboard from '../quicksight/BlueyDashboard';
import {
  SelectedOption,
  LLMTypeSelect,
  PhaseSelect,
  ToolSelectionTable,
  useToolsByPhase,
} from '../common/ExecutionScopeFields';

const TestSuite: FC = () => {
  const { addNotificationItem, notificationItems } = useNotification();
  const { canManageTestSuites } = usePermissions();
  
  // API hooks
  const { data: testSuites, isLoading: isLoadingTestSuites } = useGetTestSuitesQuery();
  const { toolsByPhase, isLoading: isLoadingTools } = useToolsByPhase();
  const [runTestSuite, { isLoading: isSubmitting }] = useRunTestSuiteMutation();
  const [updateSchedule] = useUpdateTestSuiteScheduleMutation();

  // Form state
  const [selectedTestSuite, setSelectedTestSuite] = useState<SelectedOption | null>(null);
  const [selectedLLMType, setSelectedLLMType] = useState<SelectedOption | null>(null);
  const [selectedPhase, setSelectedPhase] = useState<SelectedOption | null>(null);
  const [selectedToolIds, setSelectedToolIds] = useState<string[]>([]);

  // Options for dropdowns
  const testSuiteOptions = useMemo(() => {
    if (!testSuites) return [];
    return testSuites.map((suite: TestSuiteInfo) => ({
      label: suite.id,
      value: suite.id
    }));
  }, [testSuites]);

  // Get selected test suite details
  const selectedTestSuiteDetails = useMemo(() => {
    if (!selectedTestSuite || !testSuites) return null;
    return testSuites.find(suite => suite.id === selectedTestSuite.value);
  }, [selectedTestSuite, testSuites]);

  // Get current phase tools
  const currentPhaseTools = useMemo(() => {
    if (!toolsByPhase || !selectedPhase) return [];
    return toolsByPhase[selectedPhase.value] || [];
  }, [toolsByPhase, selectedPhase]);

  // Check if form is valid
  const isFormValid = !!(selectedTestSuite && selectedLLMType && selectedPhase && selectedToolIds.length > 0);

  // Handle form submission
  const handleSubmit = async () => {
    if (!isFormValid || !selectedPhase) return;

    try {
      const toolVersionRevision: Record<string, ToolVersionRevisionInfo> = {};
      selectedToolIds.forEach((id) => {
        const toolItem = currentPhaseTools.find(t => t.toolId === id);
        if (toolItem) {
          toolVersionRevision[id] = {
            version: toolItem.version,
            revision: toolItem.revision,
            status: toolItem.status || null
          };
        }
      });

      const request: TestExecutionRequest = {
        testSuiteId: selectedTestSuite!.value,
        executionScope: {
          phase: selectedPhase.value,
          clientLLMType: selectedLLMType!.value,
          enabledTools: selectedToolIds,
          toolVersionRevision
        }
      };

      await runTestSuite(request).unwrap();
      
      addNotificationItem({
        type: 'success',
        content: `Test suite execution started successfully for ${selectedTestSuite!.label}`,
        id: 'test-suite-executed',
      });

      setSelectedTestSuite(null);
      setSelectedLLMType(null);
      setSelectedPhase(null);
      setSelectedToolIds([]);

    } catch (error) {
      const errorMessage = (error as any)?.message || 'Unknown error occurred';
      addNotificationItem({
        type: 'error',
        content: `Failed to execute test suite: ${errorMessage}`,
        id: 'test-suite-execution-error'
      });
    }
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description="Configure and execute test suites with specific tool configurations"
        >
          Test Suite Execution
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Flashbar items={notificationItems} stackItems />
        
        {!canManageTestSuites && (
          <Alert type="info" statusIconAriaLabel="Info">
            You have view-only access to this page. Sufficient permission is required to execute test suites.
          </Alert>
        )}
        
        <Container
          header={
            <Header
              variant="h2"
              description="Configure test suite parameters and tool selection"
            >
              Test Suite Configuration
            </Header>
          }
        >
          <SpaceBetween size="l">
            <Grid
              gridDefinition={[
                { colspan: { default: 12, xs: 12, s: 6, m: 4, l: 4, xl: 4 } },
                { colspan: { default: 12, xs: 12, s: 6, m: 4, l: 4, xl: 4 } },
                { colspan: { default: 12, xs: 12, s: 12, m: 4, l: 4, xl: 4 } }
              ]}
            >
              <FormField label="Test Suite" stretch>
                <Select
                  selectedOption={selectedTestSuite}
                  onChange={({ detail }) => setSelectedTestSuite(detail.selectedOption as SelectedOption)}
                  options={testSuiteOptions}
                  placeholder="Choose a test suite"
                  disabled={isLoadingTestSuites || !canManageTestSuites}
                  loadingText="Loading test suites..."
                  empty="No test suites available"
                />
              </FormField>

              <LLMTypeSelect
                selectedOption={selectedLLMType}
                onChange={setSelectedLLMType}
                stretch
                disabled={!canManageTestSuites}
              />

              <PhaseSelect
                selectedOption={selectedPhase}
                onChange={(option) => {
                  setSelectedPhase(option);
                  setSelectedToolIds([]);
                }}
                stretch
                disabled={!canManageTestSuites}
              />
            </Grid>

            <ToolSelectionTable
              phase={selectedPhase?.value ?? null}
              toolsByPhase={toolsByPhase}
              isLoading={isLoadingTools}
              selectedToolIds={selectedToolIds}
              onSelectionChange={setSelectedToolIds}
              disabled={!canManageTestSuites}
            />

            {/* Status Summary */}
            {(selectedTestSuite || selectedLLMType || selectedPhase || selectedToolIds.length > 0) && (
              <Container
                header={<Header variant="h3">Configuration Summary</Header>}
              >
                <SpaceBetween size="s">
                  <Box>
                    <strong>Test Suite:</strong>{' '}
                    {selectedTestSuite ? (
                      <SpaceBetween direction="vertical" size="xs">
                        <StatusIndicator type="success">{selectedTestSuite.label}</StatusIndicator>
                        {selectedTestSuiteDetails?.description && (
                          <Box color="text-body-secondary" fontSize="body-s">
                            {selectedTestSuiteDetails.description}
                          </Box>
                        )}
                      </SpaceBetween>
                    ) : (
                      <StatusIndicator type="pending">Not selected</StatusIndicator>
                    )}
                  </Box>
                  <Box>
                    <strong>LLM Type:</strong>{' '}
                    {selectedLLMType ? (
                      <StatusIndicator type="success">{selectedLLMType.label}</StatusIndicator>
                    ) : (
                      <StatusIndicator type="pending">Not selected</StatusIndicator>
                    )}
                  </Box>
                  <Box>
                    <strong>Phase:</strong>{' '}
                    {selectedPhase ? (
                      <StatusIndicator type="success">{selectedPhase.label}</StatusIndicator>
                    ) : (
                      <StatusIndicator type="pending">Not selected</StatusIndicator>
                    )}
                  </Box>
                  <Box>
                    <strong>Tools:</strong>{' '}
                    {selectedToolIds.length > 0 ? (
                      <StatusIndicator type="success">{selectedToolIds.length} selected</StatusIndicator>
                    ) : (
                      <StatusIndicator type="pending">None selected</StatusIndicator>
                    )}
                  </Box>
                </SpaceBetween>
              </Container>
            )}

            {/* Submit Button */}
            <Box textAlign="right">
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!isFormValid || !canManageTestSuites}
                loading={isSubmitting}
                loadingText="Executing test suite..."
              >
                Execute Test Suite
              </Button>
            </Box>
          </SpaceBetween>
        </Container>

        <Container
          header={
            <Header
              variant="h2"
              description="Test suites with recurring schedule enabled will run automatically every day at 2 AM UTC"
            >
              Scheduled Test Suites
            </Header>
          }
        >
          <Table
            loading={isLoadingTestSuites}
            loadingText="Loading test suites..."
            items={testSuites || []}
            empty={
              <Box textAlign="center" color="inherit">
                <b>No test suites</b>
                <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                  No test suites available.
                </Box>
              </Box>
            }
            columnDefinitions={[
              {
                id: 'id',
                header: 'Test Suite ID',
                cell: (item: TestSuiteInfo) => item.id,
                sortingField: 'id',
              },
              {
                id: 'name',
                header: 'Name',
                cell: (item: TestSuiteInfo) => item.name || '-',
              },
              {
                id: 'description',
                header: 'Description',
                cell: (item: TestSuiteInfo) => item.description || '-',
              },
              {
                id: 'scheduled',
                header: 'Daily Scheduled',
                cell: (item: TestSuiteInfo) => (
                  <Toggle
                    checked={item.isScheduled}
                    disabled={!canManageTestSuites}
                    onChange={({ detail }) => {
                      updateSchedule({ testSuiteId: item.id, isScheduled: detail.checked })
                        .unwrap()
                        .then(() => {
                          addNotificationItem({
                            type: 'success',
                            content: `${item.id} ${detail.checked ? 'added to' : 'removed from'} recurring schedule`,
                            id: `schedule-${item.id}`,
                          });
                        })
                        .catch(() => {
                          addNotificationItem({
                            type: 'error',
                            content: `Failed to update schedule for ${item.id}`,
                            id: `schedule-error-${item.id}`,
                          });
                        });
                    }}
                  />
                ),
              },
            ]}
          />
        </Container>

        <BlueyDashboard />
      </SpaceBetween>
    </ContentLayout>
  );
};

export default TestSuite;
